import { AnimatedNumber } from "@/components/animated-number"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog"
import { useToast } from "@/hooks/use-toast"
import { AnimatePresence, MotionDiv, MotionSpan, useMotionValue, useSpring, useTransform } from "@/lib/framer"
import { getComparisonMetrics, type CalculatorInputs } from "@/lib/meeting-waste/calculations"
import { trackRybbitEvent } from "@/lib/rybbit"
import { Link } from "@inertiajs/react"
import { Calculator, Car, CheckCircle, DollarSign, Laptop, Send } from "lucide-react"

function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
    </svg>
  )
}

function SlackIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.528 2.528 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313zM8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312zm10.122 2.521a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.268 0a2.528 2.528 0 0 1-2.523 2.521 2.527 2.527 0 0 1-2.52-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.523 2.522v6.312zm-2.523 10.122a2.528 2.528 0 0 1 2.523 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.52-2.522v-2.522h2.52zm0-1.268a2.527 2.527 0 0 1-2.52-2.523 2.526 2.526 0 0 1 2.52-2.52h6.313A2.527 2.527 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.523h-6.313z" />
    </svg>
  )
}
import { motion } from "motion/react"
import { useEffect, useMemo, useState } from "react"

interface CalculatorResultProps {
  amount: number;
  inputs: CalculatorInputs;
}

const PUNCHLINES = {
  low: [
    { text: "That's about 2-3 conference tickets. Could've sent someone to learn something useful.", emoji: "🎫" },
    { text: "Roughly your team's annual Slack and Notion subscriptions. At least those are productive.", emoji: "📱" },
  ],
  medium: [
    { text: "That's a mid-level developer's salary. You could've hired actual help instead.", emoji: "👨‍💻" },
    { text: "Enough to cover your entire CI/CD infrastructure for 5 years. But sure, keep talking.", emoji: "⚙️" },
  ],
  high: [
    { text: "That's your entire engineering team's equipment budget. Hope those updates were worth it.", emoji: "💻" },
    { text: "You could've funded that feature everyone's been asking for. For 3 years.", emoji: "🚀" },
  ],
  extreme: [
    { text: "That's enough to hire 3 senior engineers for a year. But at least you're synced up.", emoji: "👥" },
    { text: "Your customers could've had that major feature they've been requesting since 2019.", emoji: "📋" },
  ],
  astronomical: [
    {
      text: "That's venture funding money. You could've built the entire product roadmap for next year.",
      emoji: "💰",
    },
    { text: "Congratulations! You've wasted more money than most startups raise in their seed round.", emoji: "🏆" },
  ],
};

// Ensures both punchlines in a category are used alternately before repeating
let lastPunchlineIndex: Record<keyof typeof PUNCHLINES, number | null> = {
  low: null,
  medium: null,
  high: null,
  extreme: null,
  astronomical: null,
};

function getPunchline(amount: number): { text: string; emoji: string } {
  let category: keyof typeof PUNCHLINES;

  if (amount < 50000) category = "low";
  else if (amount < 250000) category = "medium";
  else if (amount < 1000000) category = "high";
  else if (amount < 5000000) category = "extreme";
  else category = "astronomical";

  const options = PUNCHLINES[category];
  let idx: number;

  // Alternate punchlines: pick the other one if last used, else random
  if (options.length === 2) {
    const lastIdx = lastPunchlineIndex[category];
    idx = lastIdx === 0 ? 1 : 0;
    lastPunchlineIndex[category] = idx;
  } else {
    idx = Math.floor(Math.random() * options.length);
    lastPunchlineIndex[category] = idx;
  }

  return options[idx];
}

export function CalculatorResult({ amount, inputs }: CalculatorResultProps) {
  const { toast } = useToast();
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const punchline = useMemo(() => getPunchline(amount), [amount]);
  const metrics = getComparisonMetrics(amount);

  // Motion values for animated counters
  const teslaCounter = useMotionValue(0);
  const springConfig = { damping: 30, stiffness: 200 };
  const teslaSpring = useSpring(teslaCounter, springConfig);
  const gitDigestLiftimeDeals = useTransform(teslaSpring, (latest) => Math.floor(latest / 500));

  // Motion value for progress bar
  const progressMotion = useMotionValue(0);
  const progressSpring = useSpring(progressMotion, { damping: 20, stiffness: 100 });

  // Animated scale for comparison cards
  const cardScale = useMotionValue(0);

  // Motion value for macbook counter
  const macbookCounter = useMotionValue(0);
  const macbookSpring = useSpring(macbookCounter, { damping: 15, stiffness: 150 });
  const macbookDisplay = useTransform(macbookSpring, (latest) => Math.floor(latest).toString());

  const formattedAmount = `$${amount.toLocaleString()}`;

  const shareOrigin = typeof window !== "undefined" ? window.location.origin : "https://gitdigest.ai"
  const sharePath = `${shareOrigin}/meeting-waste`

  const shareData = {
    twitter: {
      text: `Our daily meetings burn ${formattedAmount}/yr 🔥 according to Git Digest's Meeting Cost Calculator by @RaulOnRails. How much are yours costing? 👉`,
      url: `${sharePath}?utm_source=twitter&utm_campaign=meeting-waste&amount=${amount}`,
    },
    linkedin: {
      text: `Our daily meetings are burning ${formattedAmount}/yr 🔥 Imagine shipping features instead of incinerating budget. How much are your meetings costing? 👉 ${sharePath}?utm_source=linkedin&utm_campaign=meeting-waste #EngineeringLeadership #Productivity`,
      url: `${sharePath}?utm_source=linkedin&utm_campaign=meeting-waste&amount=${amount}`,
    },
    slack: {
      text: `Meetings: ${inputs.numberOfTeams} teams x ${inputs.avgTeamSize} people x ${inputs.minutesPerMeeting} min = ${formattedAmount}/yr ‼️ → ${sharePath.replace(/^https?:\/\//, "")}?utm_source=slack&utm_campaign=meeting-waste&amount=${amount}`,
    },
  }

  const handleShare = (platform: "twitter" | "linkedin") => {
    trackRybbitEvent("share_meeting_waste", {
      platform,
      annual_waste: amount,
      teams: inputs.numberOfTeams,
      team_size: inputs.avgTeamSize,
    })

    const data = shareData[platform]
    const shareUrl =
      platform === "twitter"
        ? `https://twitter.com/intent/tweet?text=${encodeURIComponent(data.text)}&url=${encodeURIComponent(data.url)}`
        : `https://www.linkedin.com/sharing/share-offsite/?text=${encodeURIComponent(data.text)}`;

    window.open(shareUrl, "_blank", "width=600,height=400");
  };

  const handleCopySlack = async () => {
    try {
      await navigator.clipboard.writeText(shareData.slack.text);
      setCopiedToClipboard(true);
      toast({
        title: "Copied to clipboard!",
        description: "Ready to paste in Slack",
        variant: "success",
      });

      setTimeout(() => {
        setCopiedToClipboard(false);
        window.location.href = "slack://open";
      }, 2000);
    } catch {
      toast({
        title: "Failed to copy",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    // Start animations only after calculation is complete
    progressMotion.set(100);
    teslaCounter.set(amount);
    cardScale.set(1);
    macbookCounter.set(metrics.macbookPros);

    return () => {
      progressMotion.set(0);
      teslaCounter.set(0);
      cardScale.set(0);
      macbookCounter.set(0);
    };
  }, [amount, progressMotion, teslaCounter, cardScale, macbookCounter, metrics.macbookPros]);

  return (
    <Card className="border-golden-500/20 from-golden-500/5 via-golden-400/10 to-golden-400/10 space-y-6 overflow-hidden bg-gradient-to-br p-8 text-center">
      <div className="absolute inset-0 -z-10">
        <div className="from-golden-500/5 to-golden-400/5 absolute inset-0 animate-pulse bg-gradient-to-br" />
      </div>

      <AnimatePresence mode="wait">
        <MotionDiv key="result" initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
          <MotionDiv
            className="space-y-2"
            initial={{ scale: 0, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 100, delay: 0.2 }}
          >
            <p className="text-golden-500 text-sm font-semibold tracking-wider uppercase">Annual Meeting Costs</p>
            <div className="text-foreground flex items-center justify-center text-6xl font-bold md:text-7xl">
              <MotionDiv
                initial={{ rotate: -180, scale: 0 }}
                animate={{ rotate: 0, scale: 1 }}
                transition={{ type: "spring", stiffness: 150, delay: 0.3 }}
              >
                <DollarSign className="text-golden-500 inline-block h-12 w-12" />
              </MotionDiv>
              <AnimatedNumber start={0} end={amount} decimals={0} />
            </div>
          </MotionDiv>

          <MotionDiv
            className="mt-6 flex justify-center gap-2"
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 1.8, duration: 0.5 }}
          >
            <Dialog>
              <DialogTrigger asChild>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-golden-500/20 hover:bg-golden-500/10 hover:border-golden-500/40 gap-2"
                >
                  <Send className="h-3 w-3" />
                  Share Results 🔥
                </Button>
              </DialogTrigger>
              <DialogContent className="sm:max-w-md">
                <DialogHeader>
                  <DialogTitle>Share Results 🔥</DialogTitle>
                  <DialogDescription>Share your meeting waste calculation with others</DialogDescription>
                </DialogHeader>
                <MotionDiv
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.2, duration: 0.5 }}
                >
                  <div className="grid gap-3">
                    <Button
                      variant="outline"
                      className="justify-start hover:border-[#1DA1F2] hover:bg-[#1DA1F2]/10 hover:text-[#1DA1F2]"
                      onClick={() => handleShare("twitter")}
                    >
                      <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                      </svg>
                      Share on X
                    </Button>
                    <Button
                      variant="outline"
                      className="justify-start hover:border-[#0077B5] hover:bg-[#0077B5]/10 hover:text-[#0077B5]"
                      onClick={() => handleShare("linkedin")}
                    >
                      <LinkedInIcon className="mr-2 h-4 w-4" />
                      Share on LinkedIn
                    </Button>
                    <Button
                      variant="outline"
                      className="justify-start hover:border-purple-500 hover:bg-purple-500/10 hover:text-purple-500"
                      onClick={handleCopySlack}
                    >
                      {copiedToClipboard ? (
                        <>
                          <CheckCircle className="mr-2 h-4 w-4" />
                          Copied to clipboard! Now opening Slack...
                        </>
                      ) : (
                        <>
                          <SlackIcon className="mr-2 h-4 w-4" />
                          Copy to Slack
                        </>
                      )}
                    </Button>
                  </div>
                </MotionDiv>
              </DialogContent>
            </Dialog>
          </MotionDiv>

          {/* Punchline with bounce animation */}
          <MotionDiv
            className="text-xl font-medium md:text-2xl"
            initial={{ y: 50, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.8, type: "spring", bounce: 0.5 }}
          >
            <MotionSpan
              className="mr-2 inline-block text-3xl"
              initial={{ scale: 0 }}
              animate={{ scale: 1, rotate: [0, -10, 10, -10, 0] }}
              transition={{ delay: 1, duration: 0.5 }}
            >
              {punchline.emoji}
            </MotionSpan>
            {punchline.text}
          </MotionDiv>

          {/* Comparison Metrics with stagger and scale animation */}
          <div className="grid grid-cols-2 gap-4">
            <MotionDiv
              className="bg-background/80 border-border/50 space-y-1 rounded-lg border p-4 shadow-sm"
              initial={{ x: -50, opacity: 0, scale: 0 }}
              animate={{ x: 0, opacity: 1, scale: 1 }}
              transition={{ delay: 1.2, type: "spring", stiffness: 150 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <MotionDiv
                initial={{ scale: 0 }}
                animate={{ scale: 1, rotate: [0, 360] }}
                transition={{ delay: 1.4, duration: 0.6, ease: "easeOut" }}
              >
                <Laptop className="text-foreground mx-auto h-6 w-6" />
              </MotionDiv>
              <p className="text-foreground/80 text-sm font-medium">MacBook Pros</p>
              <MotionDiv
                initial={{ scale: 0 }}
                animate={{ scale: [0, 1.2, 1] }}
                transition={{ delay: 1.5, type: "spring", stiffness: 200 }}
              >
                <p className="text-foreground text-3xl font-bold">
                  <motion.span>{macbookDisplay}</motion.span>
                </p>
              </MotionDiv>
            </MotionDiv>

            <MotionDiv
              className="bg-background/80 border-border/50 space-y-1 rounded-lg border p-4 shadow-sm"
              initial={{ x: 50, opacity: 0, scale: 0 }}
              animate={{ x: 0, opacity: 1, scale: 1 }}
              transition={{ delay: 1.3, type: "spring", stiffness: 150 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <MotionDiv
                initial={{ scale: 0 }}
                animate={{ scale: 1, x: [0, 10, 0] }}
                transition={{ delay: 1.5, duration: 0.8, repeat: 2 }}
              >
                <Car className="text-foreground mx-auto h-6 w-6" />
              </MotionDiv>
              <p className="text-foreground/80 text-sm font-medium">Tesla Model 3s</p>
              <MotionDiv
                initial={{ scale: 0 }}
                animate={{ scale: [0, 1.2, 1] }}
                transition={{ delay: 1.6, type: "spring", stiffness: 200 }}
              >
                <p className="text-foreground text-3xl font-bold">{metrics.teslaModel3s}</p>
              </MotionDiv>
            </MotionDiv>
          </div>

          {/* Progress Bar of Doom with spring animation */}
          <MotionDiv
            className="space-y-2"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.8 }}
          >
            <MotionDiv
              className="bg-muted h-2 overflow-hidden rounded-full"
              initial={{ width: 0 }}
              animate={{ width: "100%" }}
              transition={{ delay: 2, duration: 0.5 }}
            >
              <motion.div
                className="from-golden-500 to-golden-400 h-full bg-gradient-to-r"
                style={{ width: progressSpring }}
                initial={{ x: "-100%" }}
                animate={{ x: "0%" }}
                transition={{ delay: 2.1, duration: 1, ease: "easeOut" }}
              />
            </MotionDiv>
            <p className="text-muted-foreground animate-pulse text-xs">
              Money incinerated while you watched this animation
            </p>
          </MotionDiv>

          {/* Easter egg counter with live animation */}
          <MotionDiv
            className="text-muted-foreground text-xs"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2.5 }}
          >
            <span className="font-mono">
              That's <motion.span>{gitDigestLiftimeDeals}</motion.span>x Git Digest Lifetime deals and counting...
            </span>
            <MotionDiv
              className="mt-1"
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <span className="text-golden-500">●</span> LIVE
            </MotionDiv>
          </MotionDiv>

          <MotionDiv
            initial={{ scale: 0, opacity: 0, y: 10 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            transition={{ delay: 0.5, type: "spring", stiffness: 200 }}
          >
            <Button
              asChild
              size="sm"
              variant="outline"
              className="border-golden-500/20 bg-background/80 hover:bg-golden-500/10 hover:border-golden-500/40 backdrop-blur-sm"
            >
              <Link href="/meeting-waste" className="flex items-center gap-2" aria-label="Back to Calculator">
                <Calculator className="h-3 w-3" />
                Re-Calculate
              </Link>
            </Button>
          </MotionDiv>
        </MotionDiv>
      </AnimatePresence>
    </Card>
  );
}
